import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AccountInfo } from '@azure/msal-browser';

import { HttpService } from '@services/http.service';
import { ErrorService } from './error.service';
import { SsoService } from './sso.service';

import { User } from '@app/models/user';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private currentUser$: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);

  // store the URL so we can redirect after logging in
  public redirectUrl: string = '';

  constructor(
    private httpService: HttpService,
    private errorService: ErrorService,
    private ssoService: SsoService,
  ) { }

  public getCurrentUser$(): Observable<User | null> {
    return this.currentUser$;
  }

  public getCurrentUser(): Promise<User | null> {
    return new Promise<User | null>((resolve, reject) => {
      let user = this.currentUser$.value as User;

      if (!user) {
        return this.refreshUser().then(() => {
          return resolve(this.currentUser$.value);
        }).catch((error: any) => {
          return resolve(null);
        })
      }

      return resolve(user);
    });
  }

  public refreshLegacyUser(): Promise<void> {
    return new Promise((resolve, reject) => {
      return this.checkLogin().then(() => {
        return resolve();
      }).catch((error: any) => {
        this.errorService.printError(error);
        return reject(error);
      });
    })
  }

  public refreshUser(): Promise<void> {
    return new Promise((resolve, reject) => {
      return this.httpService.get(`**/auth/checklogin`).then((user: User) => {
        if (user) {
          this.currentUser$.next(user);
          return resolve();
        }

        this.currentUser$.next(null);
        return resolve();
      }).catch((err: any) => {
        return reject(err);
      })
    })
  }

  private checkLogin(): Promise<User | null> {
    return new Promise<User | null>((resolve, reject) => {
      return this.httpService.get(`**/auth/checklogin`).then((user: User | null) => {
        if (user) {
          this.currentUser$.next(user);
          return resolve(user);
        }
      }).catch((error: any) => {
        this.errorService.printError(error);
        this.currentUser$.next(null);
        return resolve(null);
      });
    })
  }

  public precheckLogin(email: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      return this.httpService.get(`**/auth/precheck-login`, {
        email,
      }).then((response: any) => {
        return resolve(response.requiresSso);
      }).catch((error: any) => {
        return reject(error);
      });
    })
  }

  public loginUser(email: string, password: string): Promise<void> {
    return new Promise((resolve, reject) => {
      return this.httpService.post(`**/auth/login`, {
        email,
        password
      }, { fullResponse: true }).then((user: User) => {
        if (user) {
          this.currentUser$.next(user);
          return resolve();
        }

        this.currentUser$.next(null);
        return reject('Die Anmeldung ist derzeit nicht möglich.')
      }).catch((error: any) => {
        console.log(error);
        return reject(error);
      });
    })
  }

  public logoutAll(): Promise<void> {
    return new Promise((resolve, reject) => {
      return this.httpService.get(`**/auth/logout`).then((result: any) => {
        if (this.currentUser$.value?.externalAuthId) {
          this.currentUser$.next(null);
          return this.ssoService.logout();
        }

        this.currentUser$.next(null);

        return Promise.resolve();
      }).then(() => {
        return resolve();
      }).catch((error: any) => {
        this.currentUser$.next(null);

        return resolve();
      });
    })
  }

  public requestPasswordRecoverEmail(email: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {

      return this.httpService.get(`**/auth/request-password-recover-email`, {
        email: email,
      }).then((result: any) => {
        return resolve();
      }).catch((error: any) => {
        return reject();
      });
    })
  }

  public checkPasswordRecoverRequest(email: string, uid: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {

      return this.httpService.get(`**/auth/check-password-recover-request`, {
        email,
        uid,
      }).then((result: any) => {
        return resolve();
      }).catch((error: any) => {
        return reject();
      });
    })
  }

  public getPasswordRegex(): Promise<string> {
    return new Promise<string>((resolve, reject) => {

      return this.httpService.get(`**/auth/get-password-regex`, undefined, undefined, 'text').then((result: any) => {
        return resolve(result.regex);
      }).catch((error: any) => {
        return reject(error);
      });
    })
  }

  public verifyEmail(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      return this.httpService.post(`**/auth/verify-email`, { token }).then(() => {
        return resolve();
      }).catch((error: any) => {
        this.errorService.printError(error);

        return reject(error);
      })
    })
  }

  public validateIdToken(idToken: string): Promise<User> {
    return new Promise((resolve, reject) => {
      return this.httpService.post('**/auth/validate-idtoken', { idToken: idToken }).then((response) => {
        return resolve(response);
      }).catch((err) => {
        return reject(err);
      });
    });
  }

}
